import { createTheme } from '@material-ui/core/styles';
import { themeOptions } from './theme';

// Dark palette scale, kept in one place so tokens stay consistent.
const surface = {
  background: 'hsl(222, 60%, 4%)',
  paper: 'hsl(222, 47%, 7%)',
  raised: 'hsl(222, 47%, 11%)',
  overlay: 'hsl(222, 47%, 14%)',
  border: 'hsl(215, 28%, 22%)',
};

const content = {
  primary: 'hsl(210, 40%, 98%)',
  secondary: 'hsl(215, 20%, 65%)',
  accent: 'hsl(213, 94%, 78%)',
};

const accent = {
  main: 'hsl(217, 91%, 60%)',
  soft: 'rgba(59, 130, 246, 0.16)',
  subtle: 'rgba(59, 130, 246, 0.08)',
};

// These are OPTIONS, not a resolved theme — createTheme(themeOptions, darkOptions)
// deep-merges this object onto themeOptions and only then runs
// createPalette/createTypography/augmentColor, once, on the merged result. Keys
// not listed here (primary.white, secondary.lightBlue, transparent.success, all
// of typography's fontFamily/fontWeight/fontSize, breakpoints, sidebar, shadow,
// the light-mode overrides block, etc.) are inherited straight from
// `themeOptions` by the merge — they don't need to be repeated or spread here.
const darkThemeOptions = {
  palette: {
    type: 'dark',

    primary: {
      main: accent.main,
      // MUI's default augmentColor would derive `dark` from `main` by mixing in
      // ~20% black (tonalOffset), which for this hue lands too light for a
      // white icon: MuiFab / contained MuiButton use `primary.dark` for
      // `&:hover`, and the Fab/Button icon is white. At the default derived
      // lightness the icon contrast drops under the 3:1 minimum for graphical
      // objects. `dark` below is 8% deeper than `main` (52% vs 60% lightness) —
      // same hue/saturation, so the hover stays visibly the SAME blue instead of
      // shifting hue — which keeps the icon at 4.75:1 while the button itself
      // still holds 4.23:1 against the page.
      dark: 'hsl(217, 91%, 52%)',
      light: 'hsl(217, 91%, 72%)',
      mainBlue: content.accent,
      blue: accent.main,
      gray: content.secondary,
      lightGray: surface.border,
      middleGray: content.secondary,
      darkGray: content.secondary,
      snow: surface.raised,
      // `components/Button` defaults to `theme-primary-black` for its label, but
      // `primary.black` is never declared in the light options, so getColor
      // produces the string "undefined !important" there and the browser drops
      // the declaration. Declaring it here only affects dark mode; light keeps
      // its current (undeclared) behaviour.
      black: content.primary,
    },

    secondary: {
      main: surface.paper,
      gray: content.secondary,
      lightGray: surface.border,
      blue: accent.main,
      bluegray: surface.overlay,
      lightBluegray: surface.raised,
      // NOTE: `secondary.middleGray`, `secondary.darkGray` and `secondary.silver`
      // are referenced by several style files (and by `components/Button` /
      // `components/TextField` defaultProps) but are never declared in the light
      // options, so they resolve to undefined there. Declaring them here only
      // affects dark mode; light is left untouched.
      middleGray: content.secondary,
      darkGray: content.secondary,
      silver: surface.raised,
    },

    transparent: {
      main: accent.subtle,
    },

    custom: {
      // Surfaces
      pageBackground: surface.background,
      cardBackground: surface.paper,
      cardAccentBackground: accent.subtle,
      hairline: surface.border,
      // Text
      heading: content.primary,
      subheading: content.secondary,
      strongText: content.primary,
      mutedText: content.secondary,
      accentText: content.accent,
      // Buttons
      softButtonBackground: accent.soft,
      softButtonText: content.accent,
      solidButtonBackground: accent.main,
      solidButtonHoverBackground: 'hsl(217, 91%, 68%)',
      solidButtonHoverSurface: accent.soft,
      // White, not a dark surface: the label has to read on the bright blue fill.
      solidButtonText: content.primary,
      plainButtonBackground: surface.raised,
      // Tooltips
      tooltipBackground: surface.overlay,
      brandTooltipBackground: surface.overlay,
      tooltipText: content.primary,
      tooltipMutedText: content.secondary,
      // Forms
      inputBackground: surface.raised,
      inputLabel: content.secondary,
      menuItemText: content.primary,
      // Icons / charts
      iconAccent: content.accent,
      iconMuted: content.secondary,
      gaugeNeedle: content.accent,
      // Same value as the light theme on purpose — see the note in theme.js.
      folderIcon: '#ffa500',
      // Borders / surfaces
      imageBorder: surface.border,
      // Same value as the light theme on purpose — see the note in theme.js.
      // Inverting this would make white-background brand logos unreadable.
      logoSurface: '#FFFFFF',
      // Pinned: the wallet card must stay black in dark mode too, or its
      // white-on-transparent NFC icon and logo disappear. See theme.js.
      walletCardBackground: '#000000',
      walletCardText: '#FFFFFF',
      // A faint cool halo instead of the light-mode smudge: the card is black on
      // a near-black page, so it needs an ambient lift to keep its edge readable.
      walletCardShadow: 'rgba(148, 163, 184, 0.25)',
      warningBannerBackground: 'rgba(255,221,80,0.95)',
      dangerBannerBackground: 'rgba(201, 76, 76, 0.95)',
      // Both are near-black in light mode, which disappears on a dark surface;
      // they invert to the standard dark border instead.
      strongDivider: surface.border,
      strongBorder: surface.border,
      // A black wash is invisible on a dark surface; invert it.
      imagePlaceholderBackground: 'rgba(255, 255, 255, 0.08)',
      dropzoneBackground: surface.raised,
      dropzoneBorder: surface.border,
      brandButtonBackground: accent.main,
      // Hex form of `accent.main` (hsl(217, 91%, 60%)). Intercom's settings only
      // take a plain colour string, so this cannot reference the hsl() value.
      // Keep the two in sync if `accent.main` ever changes. See theme.js.
      intercomAccent: '#156AF4',
      // Diff colouring in the file-import views
      diffAddedText: 'hsl(142, 69%, 58%)',
      diffRemovedText: 'hsl(0, 84%, 68%)',
      // Linear-progress fills: the track is already type-aware (grey[700]), so
      // these only need to stay bright enough to read against it.
      progressBarInfo: accent.main,
      progressBarDanger: 'hsl(0, 84%, 68%)',
      progressBarWarning: 'hsl(48, 96%, 63%)',
      progressBarSuccess: 'hsl(142, 69%, 58%)',
      // Tables
      tableRowHover: accent.subtle,
      tableHeaderBackground: surface.raised,
      disabledInputText: content.primary,
      // Disabled / error states
      disabledText: 'hsl(215, 16%, 47%)',
      disabledBackground: 'hsl(222, 47%, 16%)',
      disabledButtonBackground: 'hsl(222, 47%, 20%)',
      disabledIcon: 'hsl(215, 20%, 35%)',
      dangerIcon: 'hsl(0, 84%, 68%)',
      errorText: 'hsl(0, 84%, 68%)',
      hoverSurface: surface.overlay,
      // Status chips: saturated text on a translucent wash of the same hue,
      // instead of the light theme's pastel fills, which glow on a dark surface.
      statusOpenText: 'hsl(142, 69%, 58%)',
      statusOpenBackground: 'rgba(52, 211, 153, 0.15)',
      statusPendingText: 'hsl(32, 95%, 64%)',
      statusPendingBackground: 'rgba(251, 146, 60, 0.15)',
      statusApprovedText: 'hsl(270, 95%, 80%)',
      statusApprovedBackground: 'rgba(192, 132, 252, 0.15)',
      statusNeutralText: content.secondary,
      statusNeutralBackground: 'rgba(148, 163, 184, 0.15)',
      statusClosedText: content.accent,
      statusClosedBackground: accent.soft,
      statusRejectedText: 'hsl(0, 84%, 68%)',
      statusRejectedBackground: 'rgba(248, 113, 113, 0.15)',
      statusIdleText: content.secondary,
      statusIdleBackground: 'rgba(100, 116, 139, 0.18)',
      statusWarningText: 'hsl(48, 96%, 63%)',
      statusWarningBackground: 'rgba(250, 204, 21, 0.15)',
      // Stays a solid fill (unlike the pastel chips, which become translucent
      // washes), but deeper than the light red so the white label keeps contrast.
      statusBlockedText: content.primary,
      statusBlockedBackground: 'hsl(0, 65%, 45%)',
      // Outlined / secondary button (light fill, colored label)
      outlineButtonBackground: surface.raised,
      outlineButtonText: content.accent,
      // Green solid button (was `secondary.green` used as a background)
      successButtonBackground: 'hsl(142, 71%, 45%)',
      successIcon: 'hsl(142, 69%, 58%)',
      // Keeps the light theme's hue but lifted, since '#2e7d32' is too dark to
      // separate from the page background here.
      exportButtonBackground: 'hsl(123, 46%, 40%)',
      // Red solid button: deeper than the light red so a light label still reads.
      dangerButtonBackground: 'hsl(0, 65%, 45%)',
      dangerAccent: 'hsl(0, 84%, 68%)',
      // Video teleaudit action buttons.
      infoButtonBackground: accent.main,
      confirmButtonBackground: 'hsl(142, 71%, 45%)',
      neutralButtonBackground: surface.overlay,
      processingButtonBackground: 'hsl(18, 90%, 55%)',
      // Sidebar: sits one step above the page background so it stays readable
      // without the bright brand blue, which is too loud on a dark surface.
      sidebarBackground: surface.paper,
      sidebarText: content.primary,
      sidebarMutedText: content.secondary,
      sidebarIcon: content.secondary,
      sidebarSelectedBackground: accent.soft,
      sidebarSelectedText: content.accent,
      sidebarSelectorBackground: surface.raised,
      sidebarSelectorText: content.primary,
      sidebarTooltipBackground: surface.overlay,
      sidebarTooltipText: content.primary,
      sidebarDividerClosed: surface.border,
      sidebarLogoutIcon: 'hsl(0, 84%, 68%)',
    },

    background: {
      default: surface.background,
      paper: surface.paper,
    },

    text: {
      primary: content.primary,
      secondary: content.secondary,
    },

    // `themeOptions` never declares `palette.action`, so before this merge MUI
    // would just generate its own light-mode defaults for it — `active` at
    // rgba(0, 0, 0, 0.54), which is what made unstyled icons (table edit
    // pencils, pagination chevrons) invisible on dark surfaces. Declaring only
    // `active` here still lets createPalette fill in the rest (hover / disabled
    // / focus) with the correct dark-mode defaults, since `type: 'dark'` above
    // is part of the same merged options.
    action: {
      // Muted rather than MUI's pure white, to mirror the light theme's intent:
      // 54% black is a muted gray, not full black.
      active: content.secondary,
    },

    // Must stay a light translucent value. MuiTableCell derives its row border
    // as darken(alpha(divider, 1), 0.68); handing it an already-dark colour
    // crushes the result to near-black and the table loses all its row lines.
    // Solid dark borders live on `custom.hairline` / `surface.border` instead.
    divider: 'rgba(255, 255, 255, 0.12)',
  },

  typography: {
    h1: { color: content.primary },
    h2: { color: content.primary },
    h3: { color: content.primary },
    h4: { color: content.secondary },
    h5: { color: content.secondary },
    h6: { color: content.primary },
    subtitle1: { color: content.accent },
  },

  overrides: {
    MuiPaper: {
      root: {
        backgroundColor: surface.paper,
      },
    },
  },
};

const darkTheme = createTheme(themeOptions, darkThemeOptions);

export default darkTheme;
